package com.partejar.app.core.security

import com.partejar.app.core.session.BiometricPreferences
import com.partejar.app.core.session.SessionLocalStateCleaner
import com.partejar.app.core.session.SessionTokenStore
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlin.time.Clock

sealed interface AppLockState {
    data object Checking : AppLockState
    data object Unlocked : AppLockState
    data object Locked : AppLockState
}

class AppLockCoordinator(
    private val tokenStore: SessionTokenStore,
    private val biometricPreferences: BiometricPreferences,
    private val sessionLocalStateCleaner: SessionLocalStateCleaner,
    private val nowMillis: () -> Long = { Clock.System.now().toEpochMilliseconds() }
) {
    private val _state = MutableStateFlow<AppLockState>(AppLockState.Checking)
    val state: StateFlow<AppLockState> = _state.asStateFlow()
    private var backgroundAtMillis: Long? = null
    private val _activationOfferRequested = MutableStateFlow(false)
    val activationOfferRequested: StateFlow<Boolean> = _activationOfferRequested.asStateFlow()
    private val _biometricEnabled = MutableStateFlow(biometricPreferences.isEnabled())
    val biometricEnabled: StateFlow<Boolean> = _biometricEnabled.asStateFlow()

    fun initialize() {
        _biometricEnabled.value = biometricPreferences.isEnabled()
        _state.value = if (tokenStore.current() != null && biometricPreferences.isEnabled()) {
            AppLockState.Locked
        } else {
            AppLockState.Unlocked
        }
    }

    fun shouldOfferActivation(authenticator: BiometricAuthenticator): Boolean =
        tokenStore.current() != null &&
            !biometricPreferences.isEnabled() &&
            !biometricPreferences.isOfferHandled() &&
            authenticator.availability() == BiometricAvailability.AVAILABLE

    fun onSessionAuthenticated() {
        _biometricEnabled.value = biometricPreferences.isEnabled()
        _activationOfferRequested.value = true
    }

    fun dismissActivationOffer() {
        biometricPreferences.markOfferHandled()
        _activationOfferRequested.value = false
    }

    suspend fun activate(authenticator: BiometricAuthenticator): BiometricAuthenticationResult {
        if (authenticator.availability() != BiometricAvailability.AVAILABLE) {
            return BiometricAuthenticationResult.Unavailable
        }
        val result = authenticator.authenticate()
        biometricPreferences.markOfferHandled()
        _activationOfferRequested.value = false
        if (result is BiometricAuthenticationResult.Success) {
            biometricPreferences.setEnabled(true)
            _biometricEnabled.value = true
        }
        return result
    }

    fun disable() {
        biometricPreferences.setEnabled(false)
        _biometricEnabled.value = false
    }

    suspend fun unlock(authenticator: BiometricAuthenticator): BiometricAuthenticationResult {
        if (authenticator.availability() != BiometricAvailability.AVAILABLE) {
            return BiometricAuthenticationResult.Unavailable
        }
        return authenticator.authenticate().also { result ->
            if (result is BiometricAuthenticationResult.Success) _state.value = AppLockState.Unlocked
        }
    }

    fun onBackground() {
        backgroundAtMillis = nowMillis()
    }

    fun onForeground() {
        val backgroundAt = backgroundAtMillis ?: return
        backgroundAtMillis = null
        if (biometricPreferences.isEnabled() && nowMillis() - backgroundAt >= LOCK_AFTER_BACKGROUND_MILLIS) {
            _state.value = AppLockState.Locked
        }
    }

    fun returnToLogin() {
        sessionLocalStateCleaner.clear()
        _biometricEnabled.value = false
        _activationOfferRequested.value = false
        _state.value = AppLockState.Unlocked
    }

    companion object {
        const val LOCK_AFTER_BACKGROUND_MILLIS = 5 * 60 * 1000L
    }
}
